import { createServerFn } from "@tanstack/react-start";
import {} from "react";
import { z } from "zod";

import type { CoachSelect } from "~/../drizzle/schema";

import { getProfileGenerator } from "../ai-profile-generator";
import { requireClerkCoachAuth } from "../coach-middleware";
import { getCoachDetails, getCoachProfile } from "../server/coach";

export const FieldInfoSchema = z.object({
  label: z.string().optional(),
  helpText: z.string().optional(),
  currentValue: z.string().optional(),
  maxLength: z.number().optional(),
});

export type FieldInfo = z.infer<typeof FieldInfoSchema>;

export interface FilledField {
  content: string;
}

export const useAiFillField = createServerFn({ method: "POST" })
  .middleware([requireClerkCoachAuth])
  .validator((data: unknown) => FieldInfoSchema.parse(data))
  .handler(
    async ({
      data: fieldInfo,
      context,
    }: {
      data: z.infer<typeof FieldInfoSchema>;
      context: { coach: CoachSelect };
    }) => {
      const { serverEnv } = await import("~/lib/server-env");
      const profileGenerator = getProfileGenerator(
        serverEnv.OPENAI_API_KEY,
        serverEnv.JINA_API_TOKEN,
        serverEnv.OPENAI_BASE_URL,
      );
      const coach = context.coach;

      if (!coach) {
        throw new Error("Coach not found");
      }

      const existingProfile = coach.profileId
        ? await getCoachProfile(coach.profileId)
        : undefined;
      const existingDetails = coach.coachDetailsId
        ? await getCoachDetails(coach.coachDetailsId)
        : undefined;

      const content = await profileGenerator.generateFieldContent(
        fieldInfo,
        coach,
        existingProfile,
        existingDetails,
      );

      return {
        content,
      };
    },
  );
